import {
  AlertCircle,
  Building2,
  Calendar,
  CheckCircle,
  Clock,
  Edit,
  FileText,
  Hash,
  Layout,
  Timer,
  Trash2,
  User
} from "lucide-react";

import LazySearchSelect from "../../../components/shared/LazySearchSelect.jsx";
import StatusBadge from "../../../components/shared/StatusBadge";
import { Badge } from "../../../components/ui/badge.jsx";
import { Button } from "../../../components/ui/button.jsx";
import { useAuth } from "../../../context/AuthContext";
import { useTaskActions } from "../../../hooks/useTaskActions";
import { cn } from "../../../lib/utils";

const isTaskOverdue = (dueDate, status) => {
  if (!dueDate || status === "COMPLETED" || status === "cancelled") return false;
  return new Date(dueDate) < new Date();
};

const getPriorityColor = (priority) => {
  const colors = {
    low: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700",
    medium:
      "bg-yale-blue-50 text-yale-blue-600 dark:bg-yale-blue-900/30 dark:text-yale-blue-400 border-yale-blue-100 dark:border-yale-blue-800",
    high: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 border-amber-100 dark:border-amber-800",
    urgent: "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400 border-rose-100 dark:border-rose-800"
  };
  return colors[priority?.toLowerCase()] || "bg-slate-100 text-slate-600 border-slate-200";
};

const TaskPreview = ({ task }) => {
  const { updateTaskStatus, reassignTask, deleteTask, editTask } = useTaskActions();
  const { user } = useAuth();

  const permissions = {
    canReassign: user?.permissions?.task?.update || false,
    canDelete: user?.permissions?.task?.delete || false,
    canEdit: user?.permissions?.task?.update || false
  };

  const isOverdue = isTaskOverdue(task.dueDate, task.status);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-950">
      {/* Header Info */}
      <div className="p-6 pb-4 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-yale-blue-500 dark:text-yale-blue-400 mb-1">
              <Hash className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold tracking-tighter uppercase">
                Task #{task.id?.toString().slice(-4) || "N/A"}
              </span>
            </div>
            <h3 className="text-xl font-extrabold leading-tight tracking-tight text-slate-900 dark:text-white">{task.title}</h3>
          </div>
          <Badge
            variant="outline"
            className={cn("shrink-0 uppercase text-[10px] px-2 py-0.5 font-bold border", getPriorityColor(task.priority))}
          >
            {task.priority || "Normal"}
          </Badge>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={task.status} className="shadow-sm" />
          {isOverdue && (
            <Badge variant="destructive" className="animate-pulse flex items-center gap-1 text-[10px] font-bold px-2">
              <AlertCircle className="w-3 h-3" />
              OVERDUE
            </Badge>
          )}
        </div>
      </div>

      <div className="px-6 space-y-6 overflow-y-auto custom-scrollbar flex-1 pb-6">
        {/* Description Section */}
        {task.description && (
          <div className="relative group">
            <div className="absolute -left-3 top-0 bottom-0 w-1 bg-yale-blue-500/20 rounded-full group-hover:bg-yale-blue-500/40 transition-colors" />
            <div className="bg-slate-50/50 dark:bg-yale-blue-900/10 p-3 rounded-lg border border-slate-100 dark:border-yale-blue-800/30">
              <div className="flex items-center gap-2 mb-2 text-slate-400 dark:text-slate-500">
                <FileText className="w-3.5 h-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-widest">Description</span>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed italic">"{task.description}"</p>
            </div>
          </div>
        )}

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <DetailItem
              icon={<Building2 className="w-4 h-4" />}
              label="Project"
              value={task.project?.name || "No Project"}
              accent
            />
            <DetailItem icon={<User className="w-4 h-4" />} label="Assigned To" value={task.assignedTo?.name || "Unassigned"} />
          </div>
          <div className="space-y-4">
            <DetailItem
              icon={<Calendar className="w-4 h-4" />}
              label="Start Date"
              value={task.startDate ? formatDate(task.startDate) : "Not Set"}
            />
            <DetailItem
              icon={<Timer className="w-4 h-4" />}
              label="Due Date"
              value={task.dueDate ? formatDate(task.dueDate) : "Not Set"}
              isOverdue={isOverdue}
            />
          </div>
        </div>

        {/* Task Progress/Status Timeline placeholder or extra info */}
        <div className="bg-gradient-to-br from-yale-blue-50 to-white dark:from-yale-blue-900/20 dark:to-slate-900 p-4 rounded-xl border border-yale-blue-100 dark:border-yale-blue-800 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Layout className="w-4 h-4 text-yale-blue-500" />
              <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Execution Status</span>
            </div>
            <span className="text-[10px] font-black text-yale-blue-600 dark:text-yale-blue-400 uppercase tracking-tighter">
              {task.status?.replace("_", " ")}
            </span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-yale-blue-800/50 h-1.5 rounded-full overflow-hidden">
            <div
              className={cn(
                "h-full transition-all duration-500",
                task.status === "COMPLETED"
                  ? "bg-emerald-500 w-full"
                  : task.status === "IN_PROGRESS"
                    ? "bg-yale-blue-500 w-1/2"
                    : "bg-slate-400 w-0"
              )}
            />
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="p-6 bg-slate-50/50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-yale-blue-900/50 backdrop-blur-md">
        <div className="flex flex-col gap-4">
          {task.status !== "COMPLETED" && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 h-10 text-xs font-bold shadow-sm"
                onClick={() => updateTaskStatus(task.id, "IN_PROGRESS")}
                disabled={task.status === "IN_PROGRESS"}
              >
                <Clock className="w-4 h-4 mr-2" />
                START TRACKING
              </Button>
              <Button
                size="sm"
                variant="primary"
                className="flex-1 h-10 text-xs font-bold"
                onClick={() => updateTaskStatus(task.id, "COMPLETED")}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                MARK COMPLETE
              </Button>
            </div>
          )}

          <div className="flex items-center gap-3">
            {permissions.canReassign && (
              <div className="flex-1">
                <LazySearchSelect
                  value={task.assignedTo?.id || ""}
                  onChange={(val) => reassignTask(task.id, val)}
                  placeholder="Reassign Resource"
                  searchPlaceholder="Search users..."
                  apiUrl="/users"
                  className="w-full"
                />
              </div>
            )}

            <div className="flex gap-2">
              {permissions.canEdit && (
                <Button size="icon" variant="edit" className="h-10 w-10 shrink-0" onClick={() => editTask(task.id)}>
                  <Edit className="w-4 h-4" />
                </Button>
              )}
              {permissions.canDelete && (
                <Button size="icon" variant="delete" className="h-10 w-10 shrink-0" onClick={() => deleteTask(task.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailItem = ({ icon, label, value, accent, isOverdue }) => (
  <div className="flex items-center gap-3 group">
    <div
      className={cn(
        "p-2 rounded-lg transition-colors",
        accent
          ? "bg-yale-blue-50 text-yale-blue-600 dark:bg-yale-blue-900/30 dark:text-yale-blue-400"
          : "bg-slate-50 text-slate-400 dark:bg-slate-900 dark:text-slate-500 group-hover:bg-slate-100 dark:group-hover:bg-slate-800"
      )}
    >
      {icon}
    </div>
    <div className="flex flex-col">
      <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest leading-none mb-1">
        {label}
      </span>
      <span
        className={cn(
          "text-sm font-semibold tracking-tight",
          isOverdue ? "text-rose-600 dark:text-rose-400 font-bold" : "text-slate-700 dark:text-slate-200"
        )}
      >
        {value}
      </span>
    </div>
  </div>
);

const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

export default TaskPreview;
